import { useEffect, useRef, useState } from "react";
import { Headphones } from "lucide-react";

/**
 * Headset accent cursor for the public site.
 * - Keeps the native cursor fully visible (accessibility) — this is a trailing accent.
 * - Disabled entirely on touch devices and when the user prefers reduced motion.
 * - Grows over interactive elements (links, buttons).
 */
export default function HeadsetCursor() {
  const [enabled, setEnabled] = useState(false);
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const pos = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });
  const hovering = useRef(false);

  useEffect(() => {
    const finePointer = window.matchMedia("(pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!finePointer || reduced) return undefined;
    setEnabled(true);

    const onMove = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      const t = e.target;
      hovering.current = !!(t.closest && t.closest("a, button, [role='button'], input, select, textarea, label"));
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px) translate(-50%, -50%) scale(${hovering.current ? 1.5 : 1})`;
      }
    };
    let raf;
    const loop = () => {
      ringPos.current.x += (pos.current.x - ringPos.current.x) * 0.14;
      ringPos.current.y += (pos.current.y - ringPos.current.y) * 0.14;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ringPos.current.x}px, ${ringPos.current.y}px) translate(-50%, -50%) scale(${hovering.current ? 1.35 : 1})`;
        ringRef.current.style.opacity = hovering.current ? "1" : "0.55";
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  if (!enabled) return null;
  return (
    <>
      <div
        ref={ringRef}
        aria-hidden="true"
        data-testid="headset-cursor-ring"
        className="pointer-events-none fixed left-0 top-0 z-[9998] flex h-11 w-11 items-center justify-center rounded-full border border-[#111111]/25 text-[#111111] transition-opacity duration-200"
        style={{ transform: "translate(-100px, -100px)" }}
      >
        <Headphones size={16} strokeWidth={1.8} />
      </div>
      <div
        ref={dotRef}
        aria-hidden="true"
        data-testid="headset-cursor-dot"
        className="pointer-events-none fixed left-0 top-0 z-[9998] h-1.5 w-1.5 rounded-full bg-[#111111]"
        style={{ transform: "translate(-100px, -100px)" }}
      />
    </>
  );
}
